import * as XLSX from "xlsx";
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import prestigeCardService from "../../../services/prestigeCardService";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import {
    Container,
    Card,
    Box,
    Typography,
    CardContent,
    Button,
    Breadcrumbs,
    IconButton,
    Modal,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Paper,
    TextField,
    InputAdornment,
    Stack,
    Checkbox,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { instanceToken } from "../../../utils/constant";
import LoadingOverlay from "../../../components/LoadingOverlay";

const styleR = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 350,
    bgcolor: "background.paper",
    borderRadius: 2,
    boxShadow: 24,
    p: 4,
};


const ExcelDateToJSDate = (serial) => {
    if (!serial) return "";
    if (typeof serial === "string") return serial;
    const utc_days = Math.floor(serial - 25569);
    const utc_value = utc_days * 86400;
    const date_info = new Date(utc_value * 1000);
    return date_info.toISOString().split('T')[0];
};

const PrestigeCards = ({ homeAlert }) => {
    const [cards, setCards] = useState([]);
    const [loading, setLoading] = useState(true);
    const [importLoading, setImportLoading] = useState(false);
    const [openR, setOpenR] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [count, setCount] = useState(0);
    const [selectedIds, setSelectedIds] = useState([]);
    const [isBulkDelete, setIsBulkDelete] = useState(false);
    const [selectAllMode, setSelectAllMode] = useState(false); // true = select all across all pages with filters

    useEffect(() => {
        fetchCards();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, rowsPerPage, searchTerm, startDate, endDate]);

    const fetchCards = async () => {
        try {
            const offset = page * rowsPerPage;
            console.log("[Frontend DEBUG] Date values:", { startDate, endDate, searchTerm });
            const res = await prestigeCardService.getPrestigeCards(
                instanceToken.token,
                rowsPerPage,
                offset,
                searchTerm,
                startDate || "",
                endDate || ""
            );
            console.log("[Frontend DEBUG] Response:", { count: res.count, cardsLength: res.cards?.length });
            setCards(res.cards);
            setCount(res.count);
            setLoading(false);
        } catch (error) {
            console.error(error);
            homeAlert("Error fetching prestige cards!", true);
            setLoading(false);
        }
    };

    const handleImportExcel = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setImportLoading(true);
        const reader = new FileReader();
        reader.onload = async (evt) => {
            try {
                const bstr = evt.target.result;
                const wb = XLSX.read(bstr, { type: "binary" });
                const wsname = wb.SheetNames[0];
                const ws = wb.Sheets[wsname];
                const data = XLSX.utils.sheet_to_json(ws, { header: 1 });

                // Find header row dynamically
                let headerRowIndex = -1;
                for (let i = 0; i < data.length; i++) {
                    const row = data[i];
                    if (row.includes("Card ID") && row.includes("Customer ID")) {
                        headerRowIndex = i;
                        break;
                    }
                }

                if (headerRowIndex === -1) {
                    throw new Error("Could not find required headers (Card ID, Customer ID) in Excel file.");
                }

                const dataRows = data.slice(headerRowIndex + 1).filter(row => row.length > 0 && row[1]); // Ensure Card ID (index 1) exists

                const mappedCards = dataRows.map(row => ({
                    name: String(row[0] || "N/A"),
                    club_id: String(row[1] || ""),
                    customer_id: String(row[2] || ""),
                    valid_until: ExcelDateToJSDate(row[3]) || "N/A",
                    policy_no: String(row[4] || ""),
                    policyholder_name: String(row[5] || ""),
                    nrc_no: String(row[6] || ""),
                    name_mm: String(row[7] || ""),
                    sum_assured: String(row[8] || ""),
                    issued_date: ExcelDateToJSDate(row[9]) || "",
                    agent_name: String(row[10] || ""),
                    agent_type: String(row[11] || ""),
                    group_team: String(row[12] || ""),
                    channel: String(row[13] || ""),
                    relationship: String(row[14] || ""),
                    tier_status: String(row[15] || ""),
                    points_balance: String(row[16] || ""),
                    remark: String(row[17] || ""),
                    card_status: String(row[18] || "Active"),
                }));

                if (mappedCards.length === 0) {
                    throw new Error("No data found in Excel starting from expected row.");
                }

                await prestigeCardService.bulkPostPrestigeCards(instanceToken.token, mappedCards);
                homeAlert(`Successfully imported ${mappedCards.length} cards!`, false);
                fetchCards();
            } catch (error) {
                console.error(error);
                homeAlert(`Import failed: ${error.message}`, true);
            } finally {
                setImportLoading(false);
                e.target.value = null; // Reset file input
            }
        };
        reader.readAsBinaryString(file);
    };

    const handleOpenDelete = (id) => {
        setSelectedId(id);
        setIsBulkDelete(false);
        setOpenR(true);
    };

    const handleOpenBulkDelete = () => {
        setIsBulkDelete(true);
        setOpenR(true);
    };

    const handleSelectAll = (event) => {
        if (event.target.checked) {
            // If there are more records than the current page, select all across pages
            if (count > cards.length) {
                setSelectAllMode(true);
                setSelectedIds(cards.map((card) => card.id));
            } else {
                // All records fit on current page, just select them
                setSelectedIds(cards.map((card) => card.id));
                setSelectAllMode(false);
            }
        } else {
            setSelectedIds([]);
            setSelectAllMode(false);
        }
    };

    const handleSelectAllAcrossPages = () => {
        setSelectAllMode(true);
    };

    const handleSelectOne = (event, id) => {
        // If in selectAllMode, exit it and select only the clicked item (or none if unchecking)
        if (selectAllMode) {
            setSelectAllMode(false);
            const wasChecked = true; // In selectAllMode, everything appears checked
            if (wasChecked) {
                // Start fresh with just this item selected (or none if they wanted to uncheck)
                setSelectedIds([id]);
            }
            return;
        }

        const selectedIndex = selectedIds.indexOf(id);
        let newSelectedIds = [];

        if (selectedIndex === -1) {
            newSelectedIds = newSelectedIds.concat(selectedIds, id);
        } else if (selectedIndex === 0) {
            newSelectedIds = newSelectedIds.concat(selectedIds.slice(1));
        } else if (selectedIndex === selectedIds.length - 1) {
            newSelectedIds = newSelectedIds.concat(selectedIds.slice(0, -1));
        } else if (selectedIndex > 0) {
            newSelectedIds = newSelectedIds.concat(
                selectedIds.slice(0, selectedIndex),
                selectedIds.slice(selectedIndex + 1)
            );
        }

        setSelectedIds(newSelectedIds);
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const formatExportData = (data) => {
        return data.map((card) => ({
            "Member Name": card.name,
            "Card ID": card.club_id,
            "Customer ID": card.customer_id,
            "Valid Until": card.valid_until,
            "Policy No": card.policy_no,
            "Policyholder Name": card.policyholder_name,
            "NRC No": card.nrc_no,
            "Name (MM)": card.name_mm,
            "Sum Assured": card.sum_assured,
            "Issued Date": card.issued_date,
            "Agent Name": card.agent_name,
            "Agent Type": card.agent_type,
            "Group/Team": card.group_team,
            "Channel": card.channel,
            "Relationship": card.relationship,
            "Tier Status": card.tier_status,
            "Points Balance": card.points_balance,
            "Remark": card.remark,
            "Card Status": card.card_status,
        }));
    };

    const exportAllExcel = async () => {
        try {
            const res = await prestigeCardService.getAllPrestigeCardsData(
                instanceToken.token,
                searchTerm,
                startDate || "",
                endDate || ""
            );
            const dataToExport = formatExportData(res.cards);
            const worksheet = XLSX.utils.json_to_sheet(dataToExport);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, "Prestige Members");
            XLSX.writeFile(workbook, `Prestige_Members_All_${new Date().toLocaleDateString()}.xlsx`);
        } catch (error) {
            console.error(error);
            homeAlert("Error exporting all data!", true);
        }
    };

    const exportToExcel = () => {
        const dataToExport = formatExportData(cards);
        const worksheet = XLSX.utils.json_to_sheet(dataToExport);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Prestige Members");
        XLSX.writeFile(workbook, `Prestige_Members_Page_${new Date().toLocaleDateString()}.xlsx`);
    };

    const handleDelete = async () => {
        try {
            if (isBulkDelete) {
                if (selectAllMode) {
                    // Delete all records matching the current filters
                    const res = await prestigeCardService.bulkDeletePrestigeCards(
                        instanceToken.token,
                        [],
                        true,
                        searchTerm,
                        startDate || "",
                        endDate || ""
                    );
                    homeAlert(`Successfully deleted ${res.deletedCount} cards!`, false);
                    setSelectAllMode(false);
                } else {
                    // Delete only selected IDs
                    await prestigeCardService.bulkDeletePrestigeCards(instanceToken.token, selectedIds);
                    homeAlert(`Successfully deleted ${selectedIds.length} cards!`, false);
                }
                setSelectedIds([]);
            } else {
                await prestigeCardService.deletePrestigeCard(instanceToken.token, selectedId);
                homeAlert("Prestige Card deleted successfully!", false);
                setSelectedIds(selectedIds.filter(id => id !== selectedId));
            }
            setOpenR(false);
            fetchCards();
        } catch (error) {
            console.error(error);
            homeAlert(isBulkDelete ? "Bulk delete failed!" : "Delete failed!", true);
        }
    };

    if (loading) {
        return <LoadingOverlay message="Loading prestige cards..." />;
    }

    return (
        <>
            <div role="presentation">
                <Breadcrumbs aria-label="breadcrumb">
                    <Typography color="text.primary">Prestige Card</Typography>
                </Breadcrumbs>
            </div>
            <Container
                maxWidth={false}
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    mt: 2,
                }}
            >
                <Box sx={{ mb: 2, display: "flex", flexDirection: { xs: "column", md: "row" }, justifyContent: "space-between", alignItems: { xs: "flex-start", md: "center" }, gap: 2 }}>
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ width: { xs: "100%", md: "auto" } }}>
                        <TextField
                            size="small"
                            variant="outlined"
                            placeholder="Search by Name, Club ID..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            sx={{ width: { xs: "100%", sm: "250px" }, backgroundColor: "white" }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon color="action" />
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <TextField
                            label="Start Date"
                            type="date"
                            size="small"
                            value={startDate || ""}
                            onChange={(e) => setStartDate(e.target.value)}
                            sx={{ backgroundColor: 'white', width: { xs: '100%', sm: '160px' } }}
                            InputLabelProps={{ shrink: true }}
                        />
                        <TextField
                            label="End Date"
                            type="date"
                            size="small"
                            value={endDate || ""}
                            onChange={(e) => setEndDate(e.target.value)}
                            sx={{ backgroundColor: 'white', width: { xs: '100%', sm: '160px' } }}
                            InputLabelProps={{ shrink: true }}
                        />
                        {(startDate || endDate || searchTerm) && (
                            <Button
                                variant="contained"
                                color="inherit"
                                onClick={() => {
                                    setStartDate(null);
                                    setEndDate(null);
                                    setSearchTerm("");
                                }}
                                sx={{ 
                                    textTransform: 'none', 
                                    fontWeight: 600,
                                    backgroundColor: '#e0e0e0',
                                    '&:hover': { backgroundColor: '#bdbdbd' },
                                    height: '40px'
                                }}
                            >
                                Clear
                            </Button>
                        )}
                    </Stack>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, justifyContent: "flex-end" }}>
                        {(selectedIds.length > 0 || selectAllMode) && (
                            <Button
                                variant="contained"
                                color="error"
                                onClick={handleOpenBulkDelete}
                                sx={{
                                    textTransform: "uppercase",
                                    fontWeight: 600,
                                    px: 3,
                                }}
                            >
                                {selectAllMode 
                                    ? `Delete All Matching (${count})` 
                                    : `Delete Selected (${selectedIds.length})`
                                }
                            </Button>
                        )}
                        <Button
                            variant="contained"
                            onClick={exportAllExcel}
                            sx={{
                                textTransform: "uppercase",
                                fontWeight: 600,
                                px: 3,
                                backgroundColor: "#2e7d32",
                                "&:hover": { backgroundColor: "#1b5e20" }
                            }}
                        >
                            Export All Excel
                        </Button>
                        <Button
                            variant="contained"
                            onClick={exportToExcel}
                            sx={{
                                textTransform: "uppercase",
                                fontWeight: 600,
                                px: 3,
                                backgroundColor: "#4caf50",
                                "&:hover": { backgroundColor: "#388e3c" }
                            }}
                        >
                            Export to Excel
                        </Button>
                        <input
                            accept=".xlsx, .xls"
                            style={{ display: "none" }}
                            id="excel-upload"
                            type="file"
                            onChange={handleImportExcel}
                        />
                        <label htmlFor="excel-upload">
                            <Button
                                variant="outlined"
                                component="span"
                                disabled={importLoading}
                                sx={{ color: "#1976d2", borderColor: "#1976d2", fontWeight: 600 }}
                            >
                                {importLoading ? "Importing..." : "Import Excel"}
                            </Button>
                        </label>
                        <Button
                            variant="contained"
                            component={Link}
                            to="/createPrestigeCard"
                            sx={{ backgroundColor: "#1976d2", fontWeight: 600 }}
                        >
                            Add New Prestige Card
                        </Button>
                    </Box>
                </Box>
                {selectedIds.length === cards.length && cards.length > 0 && count > cards.length && !selectAllMode && (
                    <Box sx={{ mb: 2, p: 1.5, backgroundColor: '#e3f2fd', borderRadius: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                            All {cards.length} records on this page are selected.
                        </Typography>
                        <Button 
                            size="small" 
                            onClick={handleSelectAllAcrossPages}
                            sx={{ textTransform: 'none', fontWeight: 600 }}
                        >
                            Select all {count} records matching filters
                        </Button>
                    </Box>
                )}
                {selectAllMode && (
                    <Box sx={{ mb: 2, p: 1.5, backgroundColor: '#fff3e0', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">
                            All {count} records matching current filters are selected.
                        </Typography>
                        <Button 
                            size="small" 
                            onClick={() => {
                                setSelectAllMode(false);
                                setSelectedIds([]);
                            }}
                            sx={{ textTransform: 'none', fontWeight: 600 }}
                        >
                            Clear selection
                        </Button>
                    </Box>
                )}
                <Card sx={{ borderRadius: 2, boxShadow: "0 4px 20px rgba(0,0,0,0.1)" }}>
                    <Box
                        sx={{
                            background: "linear-gradient(90deg, #3EABE9 0%, #1e88e5 100%)",
                            p: 2,
                            color: "white",
                        }}
                    >
                        <Typography variant="h6" fontWeight="bold">
                            Prestige Cards List
                        </Typography>
                    </Box>
                    <CardContent sx={{ p: 0 }}>
                        <TableContainer component={Box}>
                            <Table sx={{ minWidth: 650 }} aria-label="prestige card table">
                                <TableHead>
                                    <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                                        <TableCell padding="checkbox">
                                            <Checkbox
                                                indeterminate={!selectAllMode && selectedIds.length > 0 && selectedIds.length < cards.length}
                                                checked={selectAllMode || (cards.length > 0 && selectedIds.length === cards.length)}
                                                onChange={handleSelectAll}
                                            />
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: "700" }}>Name</TableCell>
                                        <TableCell sx={{ fontWeight: "700" }}>Club ID</TableCell>
                                        <TableCell sx={{ fontWeight: "700" }}>Customer ID</TableCell>
                                        <TableCell sx={{ fontWeight: "700" }}>
                                            Valid Until{" "}
                                            <Typography component="span" variant="caption" sx={{ fontWeight: 400, color: "text.secondary" }}>
                                                (YY-MM-DD)
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="center" sx={{ fontWeight: "700" }}>Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {cards.map((card) => {
                                        const isItemSelected = selectAllMode || selectedIds.indexOf(card.id) !== -1;
                                        return (
                                            <TableRow
                                                key={card.id}
                                                hover
                                                onClick={(event) => handleSelectOne(event, card.id)}
                                                role="checkbox"
                                                aria-checked={isItemSelected}
                                                selected={isItemSelected}
                                                sx={{ "&:last-child td, &:last-child th": { border: 0 }, cursor: 'pointer' }}
                                            >
                                                <TableCell padding="checkbox">
                                                    <Checkbox
                                                        checked={isItemSelected}
                                                        onChange={(event) => handleSelectOne(event, card.id)}
                                                    />
                                                </TableCell>
                                                <TableCell sx={{ fontWeight: "500" }}>{card.name}</TableCell>
                                                <TableCell>{card.club_id}</TableCell>
                                                <TableCell>{card.customer_id || "N/A"}</TableCell>
                                                <TableCell>{card.valid_until}</TableCell>
                                                <TableCell align="center">
                                                    <Box sx={{ display: "flex", justifyContent: "center", gap: 1 }}>
                                                        <IconButton
                                                            size="small"
                                                            color="primary"
                                                            component={Link}
                                                            to={`/updatePrestigeCard/${card.id}`}
                                                            title="Edit"
                                                            onClick={(e) => e.stopPropagation()}
                                                        >
                                                            <EditIcon fontSize="small" />
                                                        </IconButton>
                                                        <IconButton
                                                            size="small"
                                                            color="error"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleOpenDelete(card.id);
                                                            }}
                                                            title="Delete"
                                                        >
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                            <TablePagination
                                rowsPerPageOptions={[10, 25, 50]}
                                component="div"
                                count={count}
                                rowsPerPage={rowsPerPage}
                                page={page}
                                onPageChange={handleChangePage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                            />
                        </TableContainer>
                    </CardContent>
                </Card>
            </Container>

            <Modal open={openR} onClose={() => setOpenR(false)}>
                <Box sx={styleR}>
                    <Typography variant="h6" fontWeight="bold">
                        {isBulkDelete ? "Bulk Delete Confirmation" : "Delete Confirmation"}
                    </Typography>
                    <Typography sx={{ mt: 2, color: "text.secondary" }}>
                        {isBulkDelete 
                            ? selectAllMode
                                ? `Are you sure you want to permanently delete all ${count} prestige cards matching the current filters?`
                                : `Are you sure you want to permanently delete these ${selectedIds.length} prestige cards?`
                            : "Are you sure you want to permanently delete this prestige card?"
                        }
                    </Typography>
                    <Box sx={{ mt: 4, display: "flex", justifyContent: "flex-end", gap: 1 }}>
                        <Button onClick={() => setOpenR(false)}>Cancel</Button>
                        <Button
                            variant="contained"
                            color="error"
                            onClick={handleDelete}
                        >
                            Confirm Delete
                        </Button>
                    </Box>
                </Box>
            </Modal>
        </>
    );
};

export default PrestigeCards;
